import React, { useEffect, useRef, useState } from 'react';
import { Users, LogOut, User, ChevronLeft, ChevronRight, Package, Truck, Radio, Settings, Boxes, History, BarChart3, Home, Tag, Video, ChevronDown, ChevronUp, Activity } from 'lucide-react';
import { toast } from 'react-toastify';
import './Sidebar.css';
import { helpers } from '../../utils/helpers';
import { ADMIN_PERMISSIONS } from '../../constants/permissions';
import { authAPI } from '../../config/auth-api';
import { hasStoreFeature, getCurrentStore } from '../../constants/stores';

const Sidebar = ({ onLogout, currentPage, onPageChange, adminData, setAdminData }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [homepageExpanded, setHomepageExpanded] = useState(
    currentPage === 'homepage-banners' || currentPage === 'homepage-reels'
  );
  const [isRegionMenuOpen, setIsRegionMenuOpen] = useState(false);
  const [isSwitchingRegion, setIsSwitchingRegion] = useState(false);
  const regionMenuRef = useRef(null);

  const currentStore = getCurrentStore(adminData);
  const regions = Array.isArray(adminData?.stores) ? adminData.stores : [];
  const appName = 'Ecatcho';
  const logoLetter = (appName || 'E').charAt(0).toUpperCase();

  useEffect(() => {
    if (currentPage === 'homepage-banners' || currentPage === 'homepage-reels') {
      setHomepageExpanded(true);
    }
  }, [currentPage]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsCollapsed(true);
      }
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const closeMenu = (event) => {
      if (regionMenuRef.current && !regionMenuRef.current.contains(event.target)) {
        setIsRegionMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', closeMenu);
    return () => document.removeEventListener('mousedown', closeMenu);
  }, []);

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  const handleRegionSwitch = async (store) => {
    if (String(store.storeId) === String(currentStore?.storeId)) {
      setIsRegionMenuOpen(false);
      return;
    }
    setIsSwitchingRegion(true);
    setIsRegionMenuOpen(false);
    try {
      const result = await authAPI.switchStore(store.storeId);
      if (result.success) {
        setAdminData?.((prev) => ({
          ...prev,
          ...result.data,
          currentStore: result.data.currentStore,
          stores: result.data.stores,
          accessToken: result.data.accessToken,
        }));
        onPageChange('welcome');
      } else {
        toast.error(result.error || 'Failed to switch region');
      }
    } catch {
      toast.error('Failed to switch region');
    } finally {
      setIsSwitchingRegion(false);
    }
  };

  const canViewMenu = (menuId) => {
    const permissionMap = {
      'admins': ADMIN_PERMISSIONS.VIEW_ADMINS,
      'homepage-banners': ADMIN_PERMISSIONS.VIEW_BANNERS,
      'homepage-reels': ADMIN_PERMISSIONS.VIEW_REEL_VIDEOS,
    };

    return permissionMap[menuId] ? helpers.hasPermission(adminData, permissionMap[menuId]) : true;
  };

  const menuItems = [
    {
      id: 'dispatch',
      label: 'Dispatch',
      icon: Radio,
      page: 'dispatch-planner',
      permission: ADMIN_PERMISSIONS.VIEW_DISPATCH,
      storeFeature: 'dispatch',
    },
    {
      id: 'orders',
      label: 'Orders',
      icon: Package,
      page: 'orders',
      permission: ADMIN_PERMISSIONS.VIEW_ORDERS,
    },
    {
      id: 'reports',
      label: 'Reports',
      icon: BarChart3,
      page: 'reports',
      permission: ADMIN_PERMISSIONS.VIEW_REPORTS,
      fallbackPermission: ADMIN_PERMISSIONS.VIEW_ORDERS,
      storeFeature: 'reports',
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: Activity,
      page: 'analytics',
      permission: ADMIN_PERMISSIONS.VIEW_REPORTS,
      fallbackPermission: ADMIN_PERMISSIONS.VIEW_EVENT_LOGS,
      storeFeature: 'analytics',
    },
    {
      id: 'homepage',
      label: 'Homepage',
      icon: Home,
      hasSubmenu: true,
      storeFeature: 'homepageCms',
      submenu: [
        {
          id: 'homepage-banners',
          label: 'Banners',
          icon: Tag,
          page: 'homepage-banners',
          permissionKey: 'homepage-banners',
          permission: ADMIN_PERMISSIONS.VIEW_BANNERS,
        },
        {
          id: 'homepage-reels',
          label: 'Reel Videos',
          icon: Video,
          page: 'homepage-reels',
          permissionKey: 'homepage-reels',
          permission: ADMIN_PERMISSIONS.VIEW_REEL_VIDEOS,
        },
      ],
    },
    {
      id: 'products',
      label: 'Products',
      icon: Boxes,
      page: 'products',
      permission: ADMIN_PERMISSIONS.VIEW_PRODUCTS,
    },
    {
      id: 'drivers',
      label: 'Drivers',
      icon: Truck,
      page: 'drivers',
      permission: ADMIN_PERMISSIONS.VIEW_DRIVERS,
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      page: 'settings',
      permission: ADMIN_PERMISSIONS.VIEW_SETTINGS,
    },
    {
      id: 'event-logs',
      label: 'Admin Logs',
      icon: History,
      page: 'event-logs',
      permission: ADMIN_PERMISSIONS.VIEW_EVENT_LOGS,
    },
    {
      id: 'admins',
      label: 'Admins',
      icon: Users,
      page: 'admins',
      permission: ADMIN_PERMISSIONS.VIEW_ADMINS,
      permissionKey: 'admins',
    },
  ].filter((item) => {
    if (item.storeFeature && !hasStoreFeature(adminData, item.storeFeature)) {
      return false;
    }
    if (item.hasSubmenu) {
      const visibleSubs = (item.submenu || []).filter((sub) =>
        helpers.hasPermission(adminData, sub.permission)
        || (sub.fallbackPermission && helpers.hasPermission(adminData, sub.fallbackPermission))
      );
      item.submenu = visibleSubs;
      return visibleSubs.length > 0;
    }
    if (helpers.hasPermission(adminData, item.permission)) return true;
    if (item.fallbackPermission) {
      return helpers.hasPermission(adminData, item.fallbackPermission);
    }
    return false;
  });

  return (
    <>
      {isMobile && isCollapsed && (
        <button
          className="sidebar-mobile-menu-button"
          onClick={toggleSidebar}
          aria-label="Open menu"
        >
          <ChevronRight size={20} />
        </button>
      )}

      <div
        className={`sidebar-container ${isCollapsed ? 'sidebar-collapsed' : ''}`}
      >
        <div className="sidebar-header">
          {!isCollapsed ? (
            <div className="sidebar-logo-section">
              <div className="sidebar-logo-icon">{logoLetter}</div>
              <div className="sidebar-logo-text">
                <h2>{appName}</h2>
                <span className="sidebar-logo-subtitle">Admin Panel</span>
              </div>
            </div>
          ) : (
            <div className="sidebar-logo-collapsed">
              <div className="sidebar-logo-icon-small">{logoLetter}</div>
            </div>
          )}

          <button
            className="sidebar-toggle-button"
            onClick={toggleSidebar}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
        </div>

        <div className="sidebar-admin-info">
          {!isCollapsed ? (
            <>
              <div className="sidebar-admin-avatar">
                <User size={24} />
              </div>
              <div className="sidebar-admin-details">
                <h3>{adminData?.name || 'Admin'}</h3>
                {regions.length > 1 && currentStore && (
                  <div className="sidebar-store-dropdown" ref={regionMenuRef}>
                    <button
                      type="button"
                      className="sidebar-store-dropdown-toggle"
                      onClick={() => setIsRegionMenuOpen((open) => !open)}
                      disabled={isSwitchingRegion}
                    >
                      <span>{isSwitchingRegion ? 'Switching...' : currentStore.storeName}</span>
                      <ChevronDown size={14} />
                    </button>
                    {isRegionMenuOpen && (
                      <div className="sidebar-store-dropdown-menu">
                        {regions.map((store) => (
                          <button
                            type="button"
                            key={store.storeId}
                            className={`sidebar-store-dropdown-item ${
                              String(store.storeId) === String(currentStore.storeId)
                                ? 'sidebar-store-dropdown-item-active'
                                : ''
                            }`}
                            onClick={() => handleRegionSwitch(store)}
                          >
                            <span>{store.storeName}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                <span>{adminData?.email || ''}</span>
              </div>
            </>
          ) : (
            <div className="sidebar-admin-avatar-collapsed">
              <User size={20} />
            </div>
          )}
        </div>

        <nav className="sidebar-nav">
          {menuItems.map((item) => {
            if (item.permissionKey && !canViewMenu(item.permissionKey)) return null;

            const Icon = item.icon;

            if (item.hasSubmenu) {
              const isParentActive = (item.submenu || []).some((sub) => sub.page === currentPage);
              const isExpanded = homepageExpanded || isParentActive;

              return (
                <div key={item.id} className="sidebar-nav-item-container">
                  <button
                    className={`sidebar-nav-item ${isParentActive ? 'sidebar-nav-item-active' : ''}`}
                    onClick={() => {
                      if (isCollapsed) {
                        setIsCollapsed(false);
                        setHomepageExpanded(true);
                      } else {
                        setHomepageExpanded((prev) => !prev);
                      }
                    }}
                    title={isCollapsed ? item.label : ''}
                  >
                    <Icon size={20} />
                    {!isCollapsed && (
                      <>
                        <span style={{ flex: 1, textAlign: 'left' }}>{item.label}</span>
                        {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                      </>
                    )}
                  </button>
                  {!isCollapsed && isExpanded && (
                    <div className="sidebar-submenu">
                      {item.submenu.map((sub) => {
                        const SubIcon = sub.icon;
                        const isSubActive = currentPage === sub.page;
                        return (
                          <button
                            key={sub.id}
                            className={`sidebar-nav-item sidebar-submenu-item ${isSubActive ? 'sidebar-nav-item-active' : ''}`}
                            onClick={() => onPageChange(sub.page)}
                          >
                            <SubIcon size={18} />
                            <span>{sub.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            }

            const isActive = item.page === 'dispatch-planner'
              ? (currentPage === 'dispatch-planner' || currentPage === 'dispatch-history' || currentPage === 'dispatch')
              : currentPage === item.page;

            return (
              <div key={item.id} className="sidebar-nav-item-container">
                <button
                  className={`sidebar-nav-item ${isActive ? 'sidebar-nav-item-active' : ''}`}
                  onClick={() => onPageChange(item.page)}
                  title={isCollapsed ? item.label : ''}
                >
                  <Icon size={20} />
                  {!isCollapsed && <span>{item.label}</span>}
                </button>
              </div>
            );
          })}
        </nav>

        <div className="sidebar-footer">
          <button
            className="sidebar-logout-button"
            onClick={() => setShowLogoutConfirm(true)}
            title={isCollapsed ? 'Logout' : ''}
          >
            <LogOut size={20} />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </div>

      {showLogoutConfirm && (
        <LogoutConfirmDialog
          onConfirm={onLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}
    </>
  );
};

const LogoutConfirmDialog = ({ onConfirm, onCancel }) => {
  return (
    <div className="sidebar-confirm-overlay" onClick={onCancel}>
      <div className="sidebar-confirm-modal simple" onClick={(e) => e.stopPropagation()}>
        <h3>Log Out?</h3>
        <p>Are you sure you want to log out?</p>
        <div className="sidebar-confirm-actions">
          <button className="sidebar-confirm-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button className="sidebar-confirm-btn-logout" onClick={onConfirm}>
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
